/**
 * MatchingTable function is responsible for displaying and managing a table of taxon matching data.
 * It allows users to view, filter, and export the data, as well as generate a list of valid taxon names.
 * The option to generate a list of taxon statuses is also available if the user choosed the status tab.
 *
 * @param {Object} props - The properties object.
 * @param {FileArray} props.fileData - The data of the file being processed.
 * @param {Function} props.setTaxonList - Function to set the list of valid taxon names.
 * @param {Function} props.handleNext - Function to handle the next step in the process.
 * @param {number} props.tab - The current tab index.
 * @param {string} props.refVersion - The reference version for the taxon data.
 *
 * @returns {JSX.Element} The MatchingTable function.
 *
 * @function
 * @example
 * const fileData = { /* file data object * / };
 * const setTaxonList = (list) => { /* set taxon list function * / };
 * const handleNext = () => { /* handle next function * / };
 * const tab = 0;
 * const refVersion = 'v1';
 *
 * <MatchingTable
 *   fileData={fileData}
 *   setTaxonList={setTaxonList}
 *   handleNext={handleNext}
 *   tab={tab}
 *   refVersion={refVersion}
 * />
 */

import { useEffect, useState } from "react";
import { StoreStatus, StoreTaxaName, StoreValidTaxa } from "../../hooks/store";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import ButtonGroup from "@mui/material/ButtonGroup";
import ConsolidationTable from "@front-ends/common/src/components/app/ConsolidationTable";
import { GridFilterModel, GridRowsProp, useGridApiRef } from "@mui/x-data-grid";
import { FileArray } from "@front-ends/common/src/utils/types/FileTypes";
import { useTranslation } from "react-i18next";

export default function MatchingTable({
  fileData,
  setTaxonList,
  handleNext,
  tab,
  referentiel,
  refVersion,
  filterType
}: any) {

  const { t } = useTranslation();
  
  // Variables du store
  const { upload } = StoreStatus();
  const { isLoading: isLoadingStatus } = StoreStatus();
  const { search, response } = StoreTaxaName();
  const { find, validNameResponse } = StoreValidTaxa();

  // Initialisation des lignes du tableau pour récupérer les données après consolidation
  var initialRows: GridRowsProp = [{}];
  let values: FileArray[] = fileData.consolidateReport
    ? fileData.consolidateReport.values
    : null;
  initialRows = values?.map((item: any, index: number) => ({
    ...item,
    id: index + 1,
    isTaxonFound: item.rightValueName === item.leftValueName,
    isTaxonAlmostFound:
      item.rightValueName !== item.leftValueName &&
      item.rightValueName !== null,
    validKey: item.rightValueKey,
  }));
  const [rows, setRows] = useState<any>(initialRows);
  const [validRow, setValidRow] = useState(false);
  var ValidNameList: Array<string>;

  // Variables pour les filtres et l'api MUI
  const apiRef = useGridApiRef();
  const columnVisibilityModel = {
    isTaxonFound: false,
    isTaxonAlmostFound: false,
  };
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [activeSwitch, setActiveSwitch] = useState<string | null>(null);
  const handleSwitchChange = (switchName: string, filter: any) => {
    setActiveSwitch((prevSwitch) =>
      prevSwitch === switchName ? null : switchName
    );
    setFilterModel({
      items: activeSwitch === switchName ? [] : [filter],
    });
  };

  // Fonctions utilitaires
  function generateValidNameList(rows: any) {
    ValidNameList = rows.map((row: any) => ({
      id: row.validKey,
      name: row.rightValueName,
      validName: row.validName
    }));
    setTaxonList(ValidNameList);
  }
  function handleStatusUpload() {
    generateValidNameList(rows);
    const importType = tab === 0 ? "biblio" : "status-biblio";
    upload(fileData, importType).then(() => {
      handleNext();
    });
  }
  function handleValidName(newValue : any) {
			find(newValue, refVersion, referentiel, filterType);
  }

  // useEffect pour verifier si tout les nom valide sont remplis
  useEffect(() => {
    const rightValueName = rows.map((row: any) => row.rightValueName);
    if (rightValueName.includes(null)) {
      setValidRow(false);
    } else {
      setValidRow(true);
    }
  }, [fileData, rows]);

  return (
    <Box
      sx={{
        height: 410,
        width: "100%",
        "& .actions": {
          color: "text.secondary",
        },
        "& .textPrimary": {
          color: "text.primary",
        },
      }}
    >
      <ConsolidationTable
        rows={rows}
        setRows={setRows}
        fileData={fileData}
        referentiel={referentiel}
        refVersion={refVersion}
        filterType={filterType}
        searchTaxaName={search}
        responseTaxaName={response}
        handleValidName={handleValidName}
        responseValidName={validNameResponse}
        apiRef={apiRef}
        columnVisibilityModel={columnVisibilityModel}
        filterModel={filterModel}
        handleSwitchChange={handleSwitchChange}
        activeSwitch={activeSwitch}
        // Labels du tableau
        quotedNameLabel={t("taxa-name-column")}
        consolidatedNameLabel={t("taxa-conso-column")}
        validNameLabel={t("taxa-valid-column")}
        taxonFoundLabel={t("taxa-found-filter")}
        taxonAlmostFoundLabel={t("taxa-unsure-filter")}
        taxonNotFoundLabel={t("taxa-not-found-filter")}
        vernacularNameLabel={t("taxa-verna-column")}
        synonymyLabel={t("taxa-synonym-column")}
        taxonGroupLabel={t("taxa-group-column")}
        vernacularNameFilterLabel={t("taxa-verna-filter")}
        synonymyFilterLabel={t("taxa-synonym-filter")}
        taxonGroupFilterLabel={t("taxa-group-filter")}
        yesLabel={t("yes-text")}
        noLabel={t("no-text")}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: " center",
        }}
      >
        <ButtonGroup
          variant="outlined"
          sx={{
            mt: 2,
            mb: 2,
          }}
        >
          <Button
            startIcon={<FileDownloadIcon />}
            color="primary"
            onClick={() =>
              apiRef.current.exportDataAsCsv({
                allColumns: true,
                fileName: "Linnae_consolidation",
                delimiter: ";",
              })
            }
            variant="outlined"
          >
            {t("export-button")}
          </Button>
          {tab == 1 && (
            <Box>
              {isLoadingStatus === false && (
                <Button
                  color="primary"
                  onClick={handleStatusUpload}
                  variant="contained"
                  disabled={validRow === false}
                >
                  {t("statutes-generation-button")}
                </Button>
              )}
              {isLoadingStatus === true && (
                <LoadingButton
                  loading={true}
                  startIcon={<SaveIcon />}
                  loadingPosition="start"
                  variant="contained"
                  color="secondary"
                  sx={{ mr: 1 }}
                >
                  {t("statutes-loading-button")}
                </LoadingButton>
              )}
            </Box>
          )}
        </ButtonGroup>
      </Box>
    </Box>
  );
}
